import { useState, useEffect, useRef, useCallback } from 'react';
import { AnimatePresence, motion } from 'framer-motion';

import Slide1  from './sections/Slide1';
import Slide2  from './sections/Slide2';
import Slide3  from './sections/Slide3';
import Slide4  from './sections/Slide4';
import Slide5  from './sections/Slide5';
import Slide6  from './sections/Slide6';
import Slide7  from './sections/Slide7';
import Slide8  from './sections/Slide8';
import Slide9  from './sections/Slide9';
import Slide10 from './sections/Slide10';
import Slide11 from './sections/Slide11';
import Slide1M from './sections/mobile/Slide1M';
import Slide2M from './sections/mobile/Slide2M';
import Slide3M from './sections/mobile/Slide3M';
import Slide4M from './sections/mobile/Slide4M';
import Slide5M from './sections/mobile/Slide5M';
import Slide6M from './sections/mobile/Slide6M';
import Slide7M from './sections/mobile/Slide7M';
import Slide8M from './sections/mobile/Slide8M';
import Slide9M from './sections/mobile/Slide9M';
import Slide10M from './sections/mobile/Slide10M';
import Slide11M from './sections/mobile/Slide11M';
import SlideProgress from './components/SlideProgress';

const SLIDES_DESKTOP = [
  Slide1, Slide2, Slide3, Slide4, Slide5, Slide6,
  Slide7, Slide8, Slide9, Slide10, Slide11,
];

const SLIDES_MOBILE = [
  Slide1M, Slide2M, Slide3M, Slide4M, Slide5M, Slide6M,
  Slide7M, Slide8M, Slide9M, Slide10M, Slide11M,
];

const PRESENTATION_COUNT = 11;
const TOTAL = SLIDES_DESKTOP.length;

const pageVariants = {
  enter: (dir) => ({
    y: dir > 0 ? 40 : -40,
    opacity: 0,
  }),
  center: {
    y: 0,
    opacity: 1,
    transition: {
      y:       { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
      opacity: { duration: 0.4,  ease: 'easeOut' },
    },
  },
  exit: (dir) => ({
    y: dir > 0 ? -24 : 24,
    opacity: 0,
    transition: { duration: 0.35, ease: [0.7, 0, 0.84, 0] },
  }),
};

const pageVariantsMobile = {
  enter: {
    opacity: 0,
    scale: 0.98,
  },
  center: {
    opacity: 1,
    scale: 1,
    transition: {
      opacity: { duration: 0.4, ease: 'easeOut' },
      scale: { duration: 0.45, ease: [0.16, 1, 0.3, 1] },
    },
  },
  exit: {
    opacity: 0,
    scale: 0.98,
    transition: { duration: 0.32, ease: [0.7, 0, 0.84, 0] },
  },
};

export default function PresentationApp({ onClose }) {
  const [current, setCurrent]   = useState(0);
  const [direction, setDir]     = useState(1);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth < 768
  );

  const isAnimating = useRef(false);
  const lastTime    = useRef(0);
  const touchStartY = useRef(0);
  const mobileScrollRef = useRef(null);

  const isPresentation = current < PRESENTATION_COUNT;

  const navigate = useCallback((next) => {
    if (next < 0 || next >= TOTAL) return;
    if (isAnimating.current) return;
    setDir(next > current ? 1 : -1);
    setCurrent(next);
    isAnimating.current = true;
    setTimeout(() => { isAnimating.current = false; }, 1050);
  }, [current]);

  const debounceMs = isMobile ? 250 : 950;

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const onWheel = (e) => {
      if (isMobile) return;
      e.preventDefault();
      const now = Date.now();
      if (now - lastTime.current < debounceMs) return;
      if (isAnimating.current) return;
      const delta = Math.abs(e.deltaY) >= Math.abs(e.deltaX) ? e.deltaY : e.deltaX;
      if (Math.abs(delta) < 5) return;
      lastTime.current = now;
      navigate(delta > 0 ? current + 1 : current - 1);
    };

    const onTouchStart = (e) => { touchStartY.current = e.touches[0].clientY; };
    const onTouchEnd   = (e) => {
      const diff = touchStartY.current - e.changedTouches[0].clientY;

      if (isMobile) {
        if (Math.abs(diff) < 60) return;
        const now = Date.now();
        if (now - lastTime.current < debounceMs) return;

        const scrollEl = mobileScrollRef.current;
        if (!scrollEl) {
          lastTime.current = now;
          navigate(diff > 0 ? current + 1 : current - 1);
          return;
        }

        const { scrollTop, scrollHeight, clientHeight } = scrollEl;
        const epsilon = 2;
        const atTop = scrollTop <= epsilon;
        const atBottom = scrollTop + clientHeight >= scrollHeight - epsilon;
        const canScroll = scrollHeight > clientHeight + epsilon;

        if (!canScroll) {
          lastTime.current = now;
          navigate(diff > 0 ? current + 1 : current - 1);
          return;
        }

        if (diff > 0 && atBottom) {
          lastTime.current = now;
          navigate(current + 1);
          return;
        }
        if (diff < 0 && atTop) {
          lastTime.current = now;
          navigate(current - 1);
        }
        return;
      }

      if (Math.abs(diff) < 50) return;
      const now = Date.now();
      if (now - lastTime.current < debounceMs) return;
      lastTime.current = now;
      navigate(diff > 0 ? current + 1 : current - 1);
    };

    const onKey = (e) => {
      const now = Date.now();
      if (['ArrowDown', 'PageDown', 'ArrowRight', ' '].includes(e.key)) {
        e.preventDefault();
        if (now - lastTime.current < debounceMs) return;
        lastTime.current = now;
        navigate(current + 1);
      }
      if (['ArrowUp', 'PageUp', 'ArrowLeft'].includes(e.key)) {
        e.preventDefault();
        if (now - lastTime.current < debounceMs) return;
        lastTime.current = now;
        navigate(current - 1);
      }
      if (e.key === 'Escape' && onClose) onClose();
    };

    window.addEventListener('wheel', onWheel, { passive: false });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('touchend', onTouchEnd);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('wheel', onWheel);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchend', onTouchEnd);
      window.removeEventListener('keydown', onKey);
    };
  }, [current, navigate, onClose, isMobile, debounceMs]);

  const slideList = isMobile ? SLIDES_MOBILE : SLIDES_DESKTOP;
  const SlideComponent = slideList[current];

  return (
    <div
      className="w-full min-w-0 min-h-0 h-[100dvh] max-h-[100dvh] overflow-hidden relative bg-[#EAEAE5] pl-[env(safe-area-inset-left,0px)] pr-[env(safe-area-inset-right,0px)]"
      style={{
        touchAction: isMobile ? 'manipulation' : 'none',
        minHeight: '-webkit-fill-available',
      }}
    >
      {/* Close button */}
      {onClose && (
        <button
          onClick={onClose}
          className="absolute z-50 flex items-center gap-1.5 px-3 py-1.5 bg-[#1B2A6B]/10 hover:bg-[#1B2A6B]/20 active:bg-[#1B2A6B]/30 text-[#1B2A6B] text-[10px] font-bold tracking-widest transition-all rounded-sm top-[max(1rem,env(safe-area-inset-top,0px))] left-[max(1rem,env(safe-area-inset-left,0px))]"
          style={{ fontFamily: 'Orbitron, sans-serif' }}
        >
          ← BACK
        </button>
      )}

      <AnimatePresence custom={direction} mode="wait">
        <motion.div
          key={current}
          custom={direction}
          variants={isMobile ? pageVariantsMobile : pageVariants}
          initial="enter"
          animate="center"
          exit="exit"
          className={
            isMobile
              ? 'relative h-full min-h-0 w-full flex flex-col overflow-hidden'
              : 'absolute inset-0 min-h-0 flex flex-col'
          }
        >
          {isMobile ? (
            <div className="h-[100dvh] w-full overflow-hidden flex flex-col">
              <div
                ref={mobileScrollRef}
                className="flex-1 min-h-0 overflow-y-auto"
              >
                <SlideComponent onNavigate={navigate} currentIndex={current} />
              </div>
            </div>
          ) : (
            <div className="flex flex-1 min-h-0 w-full h-full flex-col">
              <SlideComponent onNavigate={navigate} currentIndex={current} />
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Mobile prev/next arrows — only visible on small screens */}
      <div className="md:hidden absolute inset-y-0 left-0 w-11 sm:w-12 flex items-center justify-start pl-[max(0.25rem,env(safe-area-inset-left,0px))] z-40 pointer-events-none">
        {current > 0 && (
          <button
            onClick={() => navigate(current - 1)}
            className="pointer-events-auto w-12 h-12 flex items-center justify-center rounded-full bg-[#1B2A6B]/10 active:bg-[#1B2A6B]/25 active:scale-95 transition"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <polyline points="10,3 5,8 10,13" stroke="#1B2A6B" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>
      <div className="md:hidden absolute inset-y-0 right-0 w-11 sm:w-12 flex items-center justify-end pr-[max(0.25rem,env(safe-area-inset-right,0px))] z-40 pointer-events-none">
        {current < TOTAL - 1 && (
          <button
            onClick={() => navigate(current + 1)}
            className="pointer-events-auto w-12 h-12 flex items-center justify-center rounded-full bg-[#1B2A6B]/10 active:bg-[#1B2A6B]/25 active:scale-95 transition"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <polyline points="6,3 11,8 6,13" stroke="#1B2A6B" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
      </div>

      {isPresentation && (
        <SlideProgress
          current={current + 1}
          total={PRESENTATION_COUNT}
          onNavigate={navigate}
          currentIndex={current}
        />
      )}
    </div>
  );
}
